/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { bridgeApi } from "@/lib/bridgeApi";
import { useToast } from "@/hooks/use-toast";
import { usePatientAuth } from "@/contexts/PatientAuthContext";

function typeCodeToEntityType(typeCode: number | null | undefined): string {
  switch (typeCode) {
    case 1:  return 'hospital';
    case 2:  return 'doctor';
    case 3:  return 'medical_store';
    case 4:  return 'homecare';
    case 5:  return 'ambulance';
    case 6:  return 'diagnostic';
    case 7:  return 'wholesaler';
    case 8:  return 'blood_bank';
    case 9:  return 'health_camp';
    case 10: return 'ambulance';
    default: return 'provider';
  }
}

export function useProviderLikes(providerId: string | null | undefined, typeCode?: number | null) {
  const entityType = typeCodeToEntityType(typeCode);
  const queryClient = useQueryClient();
  const { patientUser } = usePatientAuth();
  const visitorId = patientUser?.id ? String(patientUser.id) : undefined;
  const [toggling, setToggling] = useState(false);
  const key = ["provider-likes", providerId, entityType, visitorId ?? 'anon'] as const;
  const query = useQuery({
    queryKey: key,
    enabled: !!providerId,
    staleTime: 60_000,
    queryFn: async () => {
      try {
  const result = await bridgeApi.call<any>('get_likes_count', {
        entity_type: entityType,
        entity_ids: String(providerId),
              ...(visitorId ? { visitor_id: visitorId } : {}),
            });
        const id = String(providerId);
        const count = result?.data?.[id] ?? result?.[id] ?? 0;
        const liked = result?.liked_by_visitor?.[id] ?? false;
        return { count, liked };
      } catch {
        return { count: 0, liked: false };
      }
    },
  });

  const { toast } = useToast();

  const toggle = async () => {
    if (!providerId || toggling) return;
    setToggling(true);
    // Optimistic update — instantly red/un-red
    const current = query.data?.liked ?? false;
    queryClient.setQueryData(key, {
      count: (query.data?.count ?? 0) + (current ? -1 : 1),
      liked: !current,
    });
    try {
      const res = await bridgeApi.call<any>('toggle_like', {
            entity_type: entityType,
            entity_id: String(providerId),
            like_action: current ? 'unlike' : 'like',
            ...(visitorId ? { visitor_id: visitorId } : {}),
          });
      queryClient.setQueryData(key, {
        count: res?.total_likes ?? (query.data?.count ?? 0),
        liked: res?.liked ?? !current,
      });
      toast({
        title: !current ? "Saved!" : "Removed",
        description: !current ? "Added to your saved list." : "Removed from saved list.",
      });
    } catch {
      // Rollback on failure
      queryClient.setQueryData(key, { count: query.data?.count ?? 0, liked: current });
      toast({ title: "Error", description: "Could not save. Try again.", variant: "destructive" });
    } finally {
      setToggling(false);
    }
  };

  return {
    count: query.data?.count ?? 0,
    liked: query.data?.liked ?? false,
    isLoading: query.isLoading,
    toggle,
    toggling,
  };
}
